import { Controller } from "stimulus"
import Cookies from "js-cookie";
import { allowedCountries } from "../../util/allowed_countries";
import { getCSRFToken, setUpdatedCsrfMetaContent } from "../../util";

const IS_DARK = "is_dark";
export default class extends Controller {
  static targets = [
    "submitButton",
    "taxIdContainer",
    "taxIdType",
    "taxIdValue",
    "errorMessage",
  ]

  static values = {
    publishableKey: String,
    searchTaxIdTypesUrl: String,
    defaultName: String,
    defaultAddress: Object
  };

  connect() {
    this.darkModeEnabled = this._isDarkModeEnabled();
    this._initializeObserver();

    const form = document.getElementById('stripe-billing-form');
    this.customerData = new FormData(form);

    // Initialize Stripe Elements and save Default values
    this.addressElement = this._initializeStripeAddressElement();
    this._initializeCustomerData();

    this.customerCountry = this.defaultAddressValue.country;

    this.fetchTaxIdInputField();
    this.formIncomplete = true;
  }

  handleChange(e) {
    const field = e.currentTarget.name;
    const value = e.currentTarget.value;
    this.customerData.set(field, value);
  }

  handleCountryChange(e) {
    if (e.value.address.country !== this.customerCountry) {
      this.customerCountry = e.value.address.country;
      this.fetchTaxIdInputField();
    }
  }

  _initializeStripeAddressElement() {
    const stripe = Stripe(this.publishableKeyValue, { locale: I18n.locale });
    const appearance = this._formAppearance();
    const elements = stripe.elements({ appearance });
    const options = {
      mode: "billing",
      defaultValues: this._customerDataDefaultValues(),
      allowedCountries: allowedCountries //https://docs.stripe.com/js/elements_object/create_address_element#address_element_create-options-allowedCountries
    }
    const addressElement = elements.create("address", options);

    addressElement.mount("#stripe-billing-address-element");

    addressElement.on('change', (event) => {
      this.handleCountryChange(event);
      this.handleCompletedForm(event);
    });

    return addressElement;
  }

  _initializeCustomerData() {
    this.customerData.set("name", this.defaultNameValue);
    this.setCustomerDataAddress(this.defaultAddressValue);
  };

  _updateStripeAddressElement() {
    this.addressElement.unmount();
    return this._initializeStripeAddressElement();
  }

  _formAppearance() {
    return this.darkModeEnabled ? this._darkMode() : this._lightMode();
  }

  _refreshTaxIdInputValues() {
    if(this._taxIdInputFieldExists()){
      this.customerData.set("tax_id_type", this.taxIdTypeTarget.value);
      this.customerData.set("tax_id_value", this.taxIdValueTarget.value);
    } else {
      this.customerData.set("tax_id_type", "");
      this.customerData.set("tax_id_value", "");
    }
  }

  _taxIdInputFieldExists() {
    return this.taxIdContainerTarget.innerHTML.length > 0;
  }

  handleCompletedForm(e) {
    if (e.complete) {
      this.setCustomerDataAddress(e.value.address);
      this.customerData.set("name", e.value.name);
      this._toggleErrorMessage(false);
      this.formIncomplete = false;
    } else {
      this.formIncomplete = true;
    }
  }

  _customerDataDefaultValues() {
    return {
      name: this.defaultNameValue,
      address: this.defaultAddressValue
    }
  }

  fetchTaxIdInputField() {
    fetch(`${this.searchTaxIdTypesUrlValue}?country_code=${this.customerCountry}`, {
      credentials: "same-origin",
    }).then(response => response.text())
    .then(html => {
      this.taxIdContainerTarget.innerHTML = html;
      this._refreshTaxIdInputValues();
    }).catch(error => {
      console.error("Error fetching tax id input field", error);
      this.taxIdContainerTarget.innerHTML = this._renderErrorHTML();
    });
  }

  _renderErrorHTML() {
    return `
      <div class="tw-px-1 tw-text-danger-600 tw-font-normal tw-text-xs tw-mt-4 dark:tw-text-danger-400 tw-leading-5">
        <span>Oops! We encountered an issue displaying the Tax ID input field. You may still continue if you don't need to enter your Tax ID.</span>
        <span>If you need to enter your Tax ID, please reach out to us at <a href="mailto:api@coingecko.com">api@coingecko.com</a> for assistance.</span>
      </div>
    `
  }

  setCustomerDataAddress(address) {
    Object.entries(address).forEach(([key, value]) => {
      if (value == null) {
        this.customerData.set(key, "");
        return;
      }
      this.customerData.set(key, value);
    });
  }

  dispatchContinueToPaymentClickEvent() {
    this._disableSubmitButton();

    const event = new CustomEvent("continue-to-payment-click")

    document.dispatchEvent(event);
  }

  async submitForm(e) {
    if (this.formIncomplete) {
      this._toggleErrorMessage(true);
      return;
    }
    this._disableSubmitButton();

    const submitUrl = e.currentTarget.getAttribute("data-submit-url");

    await setUpdatedCsrfMetaContent();

    const options = {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": getCSRFToken(),
      },
      body: this.customerData
    }

    fetch(submitUrl, options)
      .then(response => {
        if (response.ok) {
          Modal.hide('stripe_billing_form_modal');
          this.dispatchContinueToPaymentClickEvent();
        } else if(response.status === 400) {
          response.json()
            .then(({ message } ) => {
              Modal.hide('stripe_billing_form_modal');

              // messages from: Account::Subscription::StripeBillingModalController#create
              if (message.includes("Tax Id Creation Failed")) {
                Modal.show('tax_validation_failed_modal');
              } else if (message === "Invalid address details") {
                Modal.show('invalid_address_details_modal');
              } else {
                throw new Error(`Unexpected Error message from 400 response: ${message}`);
              }
            })
            .catch(_ => {
              // if we cannot parse the response, fallback to generic error modal
              Modal.show('payment_failed_modal');
            })
        } else {
          throw new Error("Response code unrecognised, Please investigate.");
        }
        this._enableSubmitButton();
      })
      .catch(error => {
        this.show_error_modal(error);
      })
  }

  _disableSubmitButton() {
    this.submitButtonTargets.forEach(button => button.disabled = true);
  }

  _enableSubmitButton() {
    this.submitButtonTargets.forEach(button => button.disabled = false);
  }

  _toggleErrorMessage(display) {
    if (display) {
      this.errorMessageTarget.classList.remove("tw-hidden");
    } else {
      this.errorMessageTarget.classList.add("tw-hidden");
    }
  }

  show_error_modal(error) {
    Modal.hide('stripe_billing_form_modal');
    Modal.show('payment_failed_modal');
  }

  // --- UI Related Methods ---

  _isDarkModeEnabled() {
    return Cookies.get(IS_DARK) === "true";
  }

  _refreshDarkMode() {
    this.darkModeEnabled = this._isDarkModeEnabled();
    this.addressElement = this._updateStripeAddressElement();
  }

  _initializeObserver() {
    const observer = new MutationObserver(mutationsList => {
      for (var mutation of mutationsList) {
        if (mutation.type == "attributes" && mutation.attributeName === "class") {
          if (this._isDarkModeEnabled() === this.darkModeEnabled) {
            return;
          } else {
            this._refreshDarkMode();
          }
        }
      }
    })

    observer.observe(document.body, { attributes: true });
  }

  _lightMode() {
    return {
      variables: {
        borderRadius: "0.375rem",
        colorBackground: "#FFFFFF",
        colorTextPlaceholder: "#94A3B8",
        colorText: "#0F172A",
        spacingUnit: "6px",
        fontWeightNormal: "400"
      },
      rules: {
        '.Input': {
          borderWidth: "0px",
          boxShadow: "0 0 0 2px #EFF2F5",
        },
        '.Input:focus': {
          borderColor: "#4BCC00",
          boxShadow: "0 0 0 2px #4BCC00"
        },
        '.Label': {
          fontSize: "0.875rem",
          fontWeight: "600"
        }
      }
    }
  }

  _darkMode() {
    return {
      variables: {
        colorBackground: "#0D1217",
        colorTextPlaceholder: "#4A6382",
        colorText: "#DFE5EC",
        fontSizeBase: "0.875rem",
        spacingUnit: "6px",
        fontWeightNormal: "400"
      },
      rules: {
        '.Input': {
          boxShadow: "0 0 0 2px #384A61",
          borderWidth: "0px"
        },
        '.Input:focus': {
          borderColor: "#80E038",
          boxShadow: "0 0 0 2px #80E038"
        },
        '.Label': {
          fontSize: "0.875rem",
          fontWeight: "600"
        }
      }
    }
  }
}
